// Logs_Api.js
// API wrappers for the backend's file-based log viewer (/logs/*).

import {
  fastApiAuthAxios,
  FAST_API_RATE_LIMIT_MESSAGE,
} from "./Fast_interceptor";
import {
  isFastApiRateLimitError,
  getReadableApiErrorMessage,
} from "./Fast_apis";

const rateLimitedResult = () => ({
  success: false,
  rateLimited: true,
  error_code: "RATE_LIMIT_EXCEEDED",
  message: FAST_API_RATE_LIMIT_MESSAGE,
});

// GET /logs/dates -> which dates have logs, and which types (app/errors) exist per date
export const getLogDates = async () => {
  try {
    const res = await fastApiAuthAxios.get("/logs/dates");
    return res.data;
  } catch (err) {
    console.error("Error fetching log dates:", err);
    if (isFastApiRateLimitError(err)) return rateLimitedResult();
    return (
      err.response?.data || {
        success: false,
        message: getReadableApiErrorMessage(err, "Failed to load log dates"),
      }
    );
  }
};

// GET /logs - the one shared search/filter endpoint used by both User Mode
// and Developer Mode. Every returned event (in `problems`, `warnings`, and
// `entries`) carries both a raw/technical shape and a plain-language shape at
// once, so each mode just picks the fields it needs from the same response.
//
// `levels` is an array of "info" | "warning" | "error" | "critical" and is
// sent as repeated `level=` params (built manually - axios's default array
// serialization doesn't match what the backend expects: `level=a&level=b`,
// no brackets).
export const getLogs = async ({
  date,
  dateFrom,
  dateTo,
  startTime,
  endTime,
  levels,
  component,
  search,
  sort = "desc",
  limit = 200,
  offset = 0,
} = {}) => {
  try {
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (startTime) params.set("start_time", startTime);
    if (endTime) params.set("end_time", endTime);
    if (component) params.set("component", component);
    if (search) params.set("search", search);
    if (sort) params.set("sort", sort);
    if (limit != null) params.set("limit", limit);
    if (offset != null) params.set("offset", offset);
    (levels || []).forEach((level) => params.append("level", level));

    const res = await fastApiAuthAxios.get(`/logs?${params.toString()}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching logs:", err);
    if (isFastApiRateLimitError(err)) return rateLimitedResult();
    return (
      err.response?.data || {
        success: false,
        message: getReadableApiErrorMessage(err, "Failed to load logs"),
      }
    );
  }
};

// GET /logs/download?date=&type= -> streams the raw .log file and saves it in the browser
export const downloadLogFile = async ({ date, type = "app" } = {}) => {
  try {
    const res = await fastApiAuthAxios.get("/logs/download", {
      params: { date, type },
      responseType: "blob",
    });

    const disposition = res.headers?.["content-disposition"] || "";
    const nameMatch = disposition.match(/filename="?([^"]+)"?/i);
    const fileName = nameMatch?.[1] || `${date}-${type}.log`;

    const blobUrl = window.URL.createObjectURL(res.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);

    return { success: true, fileName };
  } catch (err) {
    console.error("Error downloading log file:", err);
    if (isFastApiRateLimitError(err)) return rateLimitedResult();

    // responseType "blob" means an error body (JSON) also arrives as a Blob,
    // so it needs to be read back out as text before it can be parsed/shown.
    let message = err.message;
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        message = getReadableApiErrorMessage(JSON.parse(text), message);
      } catch {
        // not JSON - keep the fallback message
      }
    } else if (err.response?.data) {
      message = getReadableApiErrorMessage(err.response.data, message);
    }

    return { success: false, message };
  }
};
